/**
 * EVERY KIND OF OPERATIONS ALERT THE PLATFORM CAN RAISE. One list, and this is
 * it (founder decision alert-titles = B, #2125, 2026-10-08).
 *
 * An `internal.alert` names its kind in `payload.kind`, and Internal Ops shows
 * every alert as its English title followed by this code in parentheses -
 * "Courier never came (COURIER_NO_SHOW)" - so an operator reads what happened
 * and can still search a log for the code. Three things hold that closed:
 *
 *  - the outbox schema (`internalAlertSchema`, outbox.ts) and the producers'
 *    `OutboxMessage` type (`OutboxMessageBody`) accept only these, so a new
 *    kind is a compile error at the `uow.enqueue` that writes it until it is
 *    added here;
 *  - `apps/internal-admin/src/i18n/alert-title.ts` indexes the English
 *    catalogue by every member, so a member without an
 *    `activity.alert.<KIND>` title fails that app's typecheck;
 *  - `scripts/check-activity-catalogue.mjs` reads this file and fails on a
 *    member without a title and on a title for a kind that is not here.
 *
 * Before this list, `kind` was any non-empty string and Internal printed an
 * untitled one as its raw code: three did, measured on 2026-10-07
 * (REVALIDATION_CANCELLED, BANK_FEED_MATCH_MISSING,
 * DOCUMENT_INTEGRITY), and two more were found while building this list:
 * CRON_JOB_FAILED, and COURIER_UNAVAILABLE - the one with 844 rows on demo.
 *
 * `UNCLASSIFIED` is the one member no producer writes on purpose: it is what a
 * STORED row reads back as when its kind is missing or is no longer on this
 * list (internal-alerts.ts, `alertFromMessage`). Its payload still carries the
 * code it was written with.
 *
 * The counter fleet's own kinds are their own closed lists, about a device and
 * about a build, and are spread in rather than restated.
 */

import { z } from 'zod';
import { HANDHELD_RELEASE_ALERT_KINDS } from './handheld-release';
import { HANDHELD_ALERT_KINDS } from './handheld-telemetry';

export const INTERNAL_ALERT_KINDS = [
  'ACCEPTANCE_TIMEOUT',
  'ASSIGNMENT_STUCK',
  'BAKERY_ISSUE_REPORTED',
  'BALANCE_DRIFT',
  'BALANCE_NEGATIVE',
  'BALANCE_SETTINGS_DRIFT',
  'BANK_FEED_MATCH_MISSING',
  'CANCELLATION_INCOMPLETE',
  'CHECKOUT_REFUND_FAILED',
  'CHECKOUT_REFUSALS_DAILY',
  'COLLECTED_AFTER_STAND_DOWN',
  'CONNECTED_ACCOUNT_MISMATCH',
  'CONNECTED_ACCOUNT_RESTRICTED',
  'COUNTER_MUTED',
  'COUNTER_UNATTENDED',
  'COURIER_CANCELLED',
  'COURIER_NO_SHOW',
  'COURIER_SEARCH_DELAYED',
  'COURIER_SEARCH_REFUSED',
  'COURIER_SEARCH_STOPPED',
  'COURIER_UNAVAILABLE',
  'CRON_JOB_FAILED',
  'DELIVERY_FAILED',
  'DELIVERY_JOB_CANCELLED',
  'DELIVERY_LOSS',
  'DELIVERY_PROVIDER_NOT_CONFIGURED',
  'DELIVERY_RECOVERED',
  'DELIVERY_RETURNING',
  'DELIVERY_SPLIT',
  'DEVICE_PIN_LOCKOUTS',
  'DOCUMENT_EXPORT_FAILED',
  'DOCUMENT_INTEGRITY',
  'GUEST_INVOICE_BLOCKED',
  'GUEST_ISSUE_REPORTED',
  'GUEST_WHATSAPP_MESSAGE',
  'KILL_SWITCH_DISENGAGED',
  'KILL_SWITCH_ENGAGED',
  'LATENESS_RISK',
  'PAYMENT_CAPTURED_AFTER_END',
  'PAYMENT_CAPTURE_FAILED',
  'PAYOUT_CANCELED',
  'PAYOUT_FAILED',
  'PAYOUT_OVERDUE',
  'PAYOUT_OVERDUE_ESCALATED',
  'PICKUP_OVER_CAPACITY',
  /** The counter handed the bag over and the provider never confirmed the pickup (BAK-007). */
  'PICKUP_UNCONFIRMED',
  'PROVIDER_CANCEL_FAILED',
  'PROVIDER_DISPATCH_REJECTED',
  'PROVIDER_EVENT_REFUSED',
  'PROVIDER_REFUND',
  'QR_CAP_RAISE_REQUESTED',
  'READINESS_ALERT_UNROUTABLE',
  'RECONCILIATION_DRIFT',
  /** The Stripe executor (issue #2272): Stripe failed a guest refund; the guest is paid another way. */
  'REFUND_FAILED',
  /** The Stripe executor's last attempt at a guest refund failed; Support > Payments retries it. */
  'REFUND_NEEDS_HUMAN',
  'REVALIDATION_CANCELLED',
  'REVALIDATION_SUPPLY_AT_RISK',
  'SELECTED_DELIVERY_PROVIDER_MISSING',
  'TABLET_HEARTBEAT_MISSING',
  'TIP_CHARGE_UNRECORDED',
  'TIP_FORWARD_FAILED',
  'TIP_WRITE_INCOMPLETE',
  'TRANSFER_AWAITING_FUNDS',
  'TRANSFER_FAILED',
  'UNALLOCATED',
  'UNATTACHED_DELIVERY_EVENT',
  'UNATTACHED_PROVIDER_REFUND',
  'UNCLASSIFIED',
  'UNKNOWN_DELIVERY_PROVIDER',
  'WHATSAPP_DELIVERY_FAILED',
  ...HANDHELD_ALERT_KINDS,
  ...HANDHELD_RELEASE_ALERT_KINDS,
] as const;

export type InternalAlertKind = (typeof INTERNAL_ALERT_KINDS)[number];

export const InternalAlertKind = z.enum(INTERNAL_ALERT_KINDS);

/** Whether a value read back from a stored row is a kind this deploy lists. */
export function isInternalAlertKind(raw: unknown): raw is InternalAlertKind {
  return InternalAlertKind.safeParse(raw).success;
}
